import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import {
	recordBylineSsrSampleOutcome,
	recordBylineSsrSampleRows,
	timeBylineSsrSample,
	type BylineSsrRoute,
} from './bylineSsrSampleMeasures.ts';
import {
	createIncomingLinksSsrState,
	INCOMING_LINKS_SAMPLE_LIMIT,
	type IncomingLinksSampleResult,
	type IncomingLinksSsrState,
} from './incomingLinksSsrState.ts';
import { preloadIncomingLinksSample } from './preloadIncomingLinksSample.ts';

type PreloadIncomingLinksSsrStateArgs = Readonly<{
	cloudId: string;
	contentId: string;
	/** Pages and blog posts are distinct graph node types. Undefined means page. */
	contentType?: BylineContentType;
	route: BylineSsrRoute;
}>;

const recordIncomingLinksSampleOutcome = (
	result: IncomingLinksSampleResult,
	route: BylineSsrRoute,
): void => {
	let outcome: string;
	if (result.status === 'unavailable') {
		outcome = result.reason;
	} else if (result.count === 0) {
		outcome = 'empty';
	} else if (result.count >= INCOMING_LINKS_SAMPLE_LIMIT) {
		outcome = 'limit-reached';
	} else {
		outcome = 'one-or-two';
	}

	recordBylineSsrSampleOutcome('incomingLinksSample', route, outcome);
};

const createStateFromSample = (
	contentId: string,
	result: IncomingLinksSampleResult,
): IncomingLinksSsrState => {
	if (result.status === 'unavailable') {
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}

	return result.count > 0
		? createIncomingLinksSsrState(contentId, 'present', result.count, result.appIds)
		: createIncomingLinksSsrState(contentId, 'empty');
};

export const preloadIncomingLinksSsrState = async ({
	cloudId,
	contentId,
	contentType,
	route,
}: PreloadIncomingLinksSsrStateArgs): Promise<IncomingLinksSsrState> => {
	let samplePromise: Promise<IncomingLinksSampleResult> | undefined;
	const getSample = (): Promise<IncomingLinksSampleResult> => {
		samplePromise ??= timeBylineSsrSample(
			'incomingLinksSample',
			route,
			recordBylineSsrSampleRows(
				'incomingLinksSample',
				route,
				preloadIncomingLinksSample({ cloudId, contentId, contentType }),
			),
		);
		return samplePromise;
	};
	const completedWithinGuard = await slowQueryGuard(
		'preloadIncomingLinksSample',
		async () => {
			await getSample();
		},
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	if (!completedWithinGuard) {
		recordBylineSsrSampleOutcome('incomingLinksSample', route, 'timeout');
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}

	const sample = await getSample();
	recordIncomingLinksSampleOutcome(sample, route);
	return createStateFromSample(contentId, sample);
};
